/**
 * The drain loop: everything queued goes out, everything changed comes back.
 *
 * Two things shape it. A batch is judged per mutation rather than as a unit, so
 * one entry the server refuses cannot wedge the queue behind it — that is why
 * the reply carries a verdict per index. And what to do about a failure depends
 * entirely on *which* failure: a train tunnel is not the mutation's fault and
 * should be retried forever, while a body the server cannot parse will be
 * refused for exactly as long as it is retried.
 */

import { ApiError } from "../lib/http.js";
import {
  applyDelta,
  endScope,
  markInflight,
  readGroups,
  readMeta,
  reconcileGroups,
  readPending,
  releaseInflight,
  settle,
  untold,
  type Change,
  type EndReason,
  type GroupLedger,
  type Resolution,
  type Settled,
  type RowWrite,
} from "./mirror.js";
import { batch, eligible, isListOrderChange, toMutation, type Pending } from "./queue.js";
import { MAX_BATCH, MAX_SYNC_BODY_BYTES, pull, push, type Mutation, type MutationResult, type Delta, type GroupAck, type ReplyScope } from "./transport.js";
import type { Item, List } from "../lib/api.js";
import { MAX_GROUP_CHILDREN, MAX_PUSH_GROUPS, type SyncActionReceipt, type SyncGroupInput, type SyncGroupReply } from "../../api/src/shared/activity.js";

/**
 * `held` and `ended` belong to a shared scope (g71). Held is waiting on the
 * person: a name to give, a sign-in that expired, someone else now using this
 * browser. Nothing is lost and `resume()` lifts it. Ended is a link that stopped
 * working: what was queued is marked unsent and nothing syncs again.
 */
export type SyncStatus = "idle" | "syncing" | "offline" | "paused" | "failed" | "held" | "ended";

/** Why a shared scope is waiting on the person (g71). */
/**
 * Why a shared scope waits for the person (g71). `not_added` is an added list
 * (g132) this account no longer holds, removed on another device: the link
 * may still work, so its copy is held, never ended.
 */
export type HoldReason = "session_expired" | "name_required" | "visitor_changed" | "not_added";

export type { EndReason } from "./mirror.js";

export type SyncEvent =
  | { type: "status"; status: SyncStatus }
  | { type: "changes"; changes: Change[] }
  /**
   * Whether anything is waiting to go out. A boolean rather than a count: the
   * only number available is records in the queue, and a record is not a change
   * a person made — a move takes its own record (`item:move:<id>`), so one row
   * renamed and dragged is two. Nothing downstream needs a number, and a count
   * would be a second thing to keep right.
   */
  | { type: "pending"; pending: boolean }
  | { type: "problem"; message: string }
  /** A shared list's answer said who is looking and what they may do (g71). */
  | { type: "scope"; scope: ReplyScope }
  | { type: "held"; reason: HoldReason }
  | { type: "ended"; reason: EndReason };

export interface Engine {
  /** Single-flight: a call while one is running joins it rather than racing it. */
  sync(): Promise<void>;
  /**
   * Something has just been put in the queue. Called by whoever enqueues, so
   * the pending flag turns true the moment a write exists rather than when a
   * pass next gets round to reading the queue — which may be never, since a
   * paused queue does not run passes at all.
   *
   * One-way on purpose: this is the only thing that sets the flag true, and a
   * drained pass is the only thing that sets it false. Two writers to one flag
   * is how the two answers drift apart.
   */
  queued(): void;
  /**
   * That write has reached IndexedDB. Until every announced write has, no read
   * of the queue may conclude it is empty — it would be reading a moment the
   * write had not arrived in yet. Must be called even when the write *failed*,
   * or nothing can ever report a drained queue again.
   */
  durable(): void;
  /** Lifts the pause a 401 put the queue into, after the caller has signed in again. */
  resume(): void;
  status(): SyncStatus;
  /** A pass is running. An open list's timer never starts a second one (g71). */
  busy(): boolean;
  /**
   * Immediate, and it does not wait for a request in flight — it cannot, since
   * the request that has to be abandoned is exactly the one that is never going
   * to answer. Nothing touches the database after this returns.
   */
  stop(): void;
}

export interface Transport {
  pull: typeof pull;
  push: typeof push;
}

export interface EngineOptions {
  db: IDBDatabase;
  emit: (event: SyncEvent) => void;
  transport?: Transport;
  /** Injected so tests can drive the backoff without a clock. */
  wait?: (ms: number) => Promise<void>;
  resolve?: (resolutions: Resolution[], delta?: Delta) => Promise<Change[]>;
  /**
   * Whose queue this engine drains (g107). A whole action names who queued
   * it, and one that names anyone else is held, never sent: each scope has
   * its own database, so this can only be a fault, and a fault must not
   * become someone else's action.
   */
  actorScope?: string;
  flushLocal?: () => Promise<void>;
  /**
   * An account's mirror pulls from 0 until it holds every list's `sharing`
   * (g71), as it does for creators (g68). A shared scope's lists never carry it.
   */
  requireSharing?: boolean;
  /**
   * The shared scope this engine serves (g71). Every answer must name the same
   * list, link and generation, and the same visitor, or nothing in it is kept.
   */
  expect?: { listId: string; permission: string; generation: string; visitor: string | null };
}

/** Being offline is not a failure to give up on; it is a state to sit out. */
const OFFLINE_BACKOFF = [1_000, 2_000, 5_000, 15_000, 30_000, 60_000];
/** A server that is broken now may be fixed in a minute, but the next trigger can ask. */
const SERVER_BACKOFF = [1_000, 2_000, 4_000, 8_000];

export function backoff(attempt: number, klass: "offline" | "server"): number {
  const schedule = klass === "offline" ? OFFLINE_BACKOFF : SERVER_BACKOFF;
  const base = schedule[Math.min(attempt, schedule.length - 1)] ?? 1_000;
  // Jittered, so a server coming back up is not met by every device at once.
  return Math.round(base * (0.8 + Math.random() * 0.4));
}

type Failure = "offline" | "server" | "auth" | "poison" | HoldReason | "unavailable" | "forbidden";

function classify(error: unknown): Failure {
  if (!(error instanceof ApiError)) return "server";
  if (error.offline) return "offline";
  // By code before status: a shared scope's refusals are 403, 404 and 409,
  // and read as poison they would revert the person's changes, or pull again
  // forever from a link that no longer opens anything (g71).
  if (error.code === "share_unavailable") return "unavailable";
  if (error.code === "share_read_only") return "forbidden";
  if (error.code === "session_expired" || error.code === "name_required" || error.code === "visitor_changed" || error.code === "not_added") return error.code;
  // Another account signed in since this page opened its copy. The same as a
  // lost session: nothing is discarded, and nothing is sent as them.
  if (error.code === "account_changed") return "auth";
  if (error.status === 401) return "auth";
  if (error.status === 408 || error.status === 429 || error.status >= 500) return "server";
  if (error.status >= 400) return "poison";
  return "server";
}

class ProtocolError extends Error {}

/**
 * Not a failure — the engine was stopped. Thrown instead of returned so a
 * half-finished pass unwinds from wherever it is, rather than each step having
 * to remember to check. Nothing after it touches the database, which is what
 * lets sign-out close and delete it without waiting for a request that may
 * never answer.
 */
class Stopped extends Error {}

export function createEngine(options: EngineOptions): Engine {
  const { db } = options;
  // A stopped engine says nothing more, even from a pass that was already
  // past its last database call: a closed store's maps and timers must not be
  // filled or restarted by an answer that arrived late (g71).
  const emit = (event: SyncEvent) => {
    if (!stopped) options.emit(event);
  };
  const transport = options.transport ?? { pull, push };
  const wait = options.wait ?? ((ms: number) => new Promise<void>((r) => setTimeout(r, ms)));
  const resolve = options.resolve ?? ((resolutions: Resolution[], delta?: Delta) => settle(db, resolutions, delta));

  let status: SyncStatus = "idle";
  let running: Promise<void> | undefined;
  let again = false;
  let paused = false;
  let stopped = false;
  /** A shared scope whose link stopped working. Never lifted (g71). */
  let ended = false;
  let pending: boolean | undefined;
  /**
   * Why a read of the queue cannot simply be believed when it comes back empty.
   *
   * A write is announced the moment the screen is told, and reaches IndexedDB a
   * few ticks later. In between, `readPending` legitimately returns nothing —
   * so a pass that reads the queue in that window concludes "empty" over a
   * mutation that is about to exist, and the line says "Synced" over somebody's
   * typing. With the server gone, nothing comes along afterwards to correct it.
   *
   * `outstanding` counts writes announced but not yet durable; `enqueues` is
   * bumped by each one so a read that spans an enqueue can tell. Raising the
   * flag is always safe. Lowering it has to be earned by a read with neither.
   */
  let outstanding = 0;
  let enqueues = 0;
  let wake: (() => void) | undefined;
  /**
   * Normally the server's cap. Dropped to one to isolate a mutation the server
   * refuses outright, so a single bad record costs itself rather than the four
   * hundred and ninety-nine queued behind it.
   */
  let limit = MAX_BATCH;

  function announce(next: SyncStatus): void {
    if (status === next) return;
    status = next;
    emit({ type: "status", status });
  }

  function report(changes: Change[]): void {
    if (changes.length > 0) emit({ type: "changes", changes });
  }

  /** Emitted on transition only, so an idle app is not told the same thing forever. */
  function notePending(next: boolean): void {
    if (pending === next) return;
    pending = next;
    emit({ type: "pending", pending });
  }

  /**
   * What a read of the queue is allowed to conclude. Finding rows always means
   * pending; finding none means pending only if nothing was enqueued since
   * `since` was taken. Raising is always safe, lowering is what has to be
   * earned.
   */
  function notePendingFrom(count: number, since: number): void {
    if (count > 0) notePending(true);
    else if (outstanding === 0 && since === enqueues) notePending(false);
  }

  /** Interruptible: coming back online should not wait out a minute of backoff. */
  async function sit(ms: number): Promise<void> {
    await Promise.race([
      wait(ms),
      new Promise<void>((resolve) => {
        wake = resolve;
      }),
    ]);
    wake = undefined;
  }

  /**
   * Every call into the mirror goes through here. The check and the call are in
   * one tick, so there is no window in which the database can be closed between
   * deciding to use it and using it.
   */
  function live<T>(run: () => Promise<T>): Promise<T> {
    if (stopped) throw new Stopped();
    return run();
  }

  /** Held for the person (g71). Nothing is discarded; `resume()` lifts it. */
  function hold(reason: HoldReason, message?: string): void {
    paused = true;
    announce("held");
    emit({ type: "held", reason });
    if (message) emit({ type: "problem", message });
  }

  /** Stops for good, without touching the database. */
  function stopAt(reason: EndReason): void {
    ended = true;
    paused = true;
    announce("ended");
    emit({ type: "ended", reason });
  }

  /** A link that stopped working: its queue is marked unsent, in the database first. */
  async function finish(reason: EndReason): Promise<void> {
    await live(() => endScope(db, reason));
    stopAt(reason);
  }

  /**
   * A shared answer is kept only when it names the scope this engine serves:
   * the same list, link and generation, and the same visitor (g71). A cookie
   * that changed under the page makes the server answer as someone else, and
   * their view of the list must not be filed under this visitor's copy.
   */
  async function refused(reply: Delta): Promise<boolean> {
    const expect = options.expect;
    if (!expect) return false;
    const scope = reply.scope;
    if (!scope || scope.listId !== expect.listId || scope.permission !== expect.permission || scope.generation !== expect.generation) {
      await finish("scope_changed");
      return true;
    }
    if ((scope.visitor.creatorId ?? null) !== expect.visitor) {
      hold(scope.visitor.sessionExpired ? "session_expired" : "visitor_changed");
      return true;
    }
    emit({ type: "scope", scope });
    return false;
  }

  /** See `EngineOptions.actorScope`. A record that names no one is from before g107 and is this scope's. */
  const mine = (record: Pending) => record.actorScope === undefined || options.actorScope === undefined
    || record.actorScope === options.actorScope;

  let reconciled = false;

  async function pass(): Promise<void> {
    let attempt = 0;
    // See the push that carries groups and no mutations, below.
    let unknowns = 0;
    let records: Pending[] = [];
    let inflight: number[] = [];
    let active: Pending[] = [];
    const deferred = new Set<number>();

    while (!stopped && !paused) {
      try {
        await options.flushLocal?.();
        // Inside the try: a mirror read that fails is a sync that failed, not
        // an exception for nobody to catch.
        const seenEnqueues = enqueues;
        // Pulled from 0 until a reply has carried creators (g68): rows kept
        // from before then have no creator and would never be re-sent.
        const meta = await live(() => readMeta(db));
        if (meta.ended) {
          // Ended in an earlier session: what is queued stays unsent.
          records = await live(() => readPending(db));
          notePendingFrom(records.length, seenEnqueues);
          stopAt(meta.ended.reason);
          return;
        }
        const cursor = meta.creatorsSynced && (meta.sharingSynced || !options.requireSharing) ? meta.cursor : 0;
        const all = await live(() => readPending(db));
        // Still pending, so the queue never reads as empty; never sent again.
        notePendingFrom(all.length, seenEnqueues);
        records = all.filter((record) => !record.unsent && mine(record));
        inflight = [];
        active = [];

        announce("syncing");

        // What this device has to say of its groups (g108), only to a server that takes them.
        let ledgers = meta.recordingGroups ? await live(() => readGroups(db)) : [];
        // Only where a group is held, until every one has been looked at: a clear
        // the page closed on part of the way through says what it never queued. One
        // opened in the last minute may still be writing, and is looked at again.
        if (ledgers.length > 0 && !reconciled) {
          reconciled = !(await live(() => reconcileGroups(db)));
          ledgers = await live(() => readGroups(db));
        }

        // Removed from the account's Lists on this device (g132): said with every push until a reply
        // carrying the set has taken them. A shared scope has no Lists to remove from.
        const removing = options.expect ? [] : meta.removing;
        const pushing = (mutations: Mutation[], groups: SyncGroupInput[]) => removing.length
          ? transport.push(cursor, mutations, groups, removing)
          : groups.length ? transport.push(cursor, mutations, groups) : transport.push(cursor, mutations);

        if (records.length === 0) {
          // Nothing queued, and a group still has something to say: its manifest,
          // the children it will never send, or a last question of whether it
          // closed. A push with no mutations carries it, and the pass ends on its
          // reply, so a server that keeps answering the same way is not asked in a loop.
          const speaking = sayGroups(ledgers, []).groups;
          if (speaking.length > 0 || removing.length > 0) {
            const reply = await pushing([], speaking);
            if (await refused(reply)) return;
            reply.from = cursor;
            if (removing.length) reply.removedSent = removing;
            reply.acks = acknowledged(speaking, reply.groups);
            report(await live(() => resolve([], reply)));
            // A group the server did not know is declared again, in this pass, once.
            if (reply.acks.some((ack) => ack.status === "unknown")) { unknowns += 1; if (unknowns <= 1) continue; }
            limit = MAX_BATCH;
            announce("idle");
            return;
          }
          const delta = await transport.pull(cursor);
          if (await refused(delta)) return;
          delta.from = cursor;
          report(await live(() => applyDelta(db, delta)));
          // Nothing left to isolate, so any narrowing from a past refusal ends
          // here rather than quietly costing a round trip per mutation forever.
          limit = MAX_BATCH;
          announce("idle");
          return;
        }

        const packed = batch(eligible(records, deferred), limit, ledgers.length ? GROUP_RESERVE_BYTES : 0)[0] ?? [];
        // A command that travels alone takes no groups with it.
        const solo = packed.length === 1 && (packed[0]!.op === "import" || isListOrderChange(packed[0]!));
        const { head, groups: speaking } = solo ? { head: packed, groups: [] } : sayGroups(ledgers, packed);
        active = head;
        if (!head.length) {
          if (attempt >= SERVER_BACKOFF.length) { announce("failed"); return; }
          deferred.clear();
          await sit(backoff(attempt++, "server"));
          continue;
        }
        inflight = head.map((record) => record.seq);
        await live(() => markInflight(db, inflight));

        const sent = head.map(toMutation);
        if (new TextEncoder().encode(JSON.stringify({ since: cursor, mutations: sent, groups: speaking })).byteLength > MAX_SYNC_BODY_BYTES) {
          throw new ApiError(413, "body_too_large", "This queued change exceeds the sync request limit.");
        }
        // With no group to speak of, the push is the call it always was.
        const reply = await pushing(sent, speaking);
        if (await refused(reply)) return;
        reply.from = cursor;
        if (removing.length) reply.removedSent = removing;
        reply.acks = acknowledged(speaking, reply.groups);

        const { resolutions, problems } = reconcile(head, sent, reply.results);
        // Receipt replays may refer to rows older than the cursor. Include their
        // current rows from the checked verdict before advancing that cursor.
        for (const result of reply.results) {
          if (result.op === "list.import" && result.status === "applied" && "items" in result) {
            reply.lists = [...reply.lists.filter(row => row.id !== result.id), result.list];
            const ids = new Set(result.items.map(row => row.id));
            reply.items = [...reply.items.filter(row => !ids.has(row.id)), ...result.items];
          }
        }
        report(await live(() => resolve(resolutions, reply)));
        inflight = [];

        // One banner per drain, not one per rejection: five items that all
        // belonged to a list another device deleted is one thing that happened.
        if (problems[0] !== undefined) emit({ type: "problem", message: problems[0] });

        attempt = 0;
        // A rewind is a promise to fetch again, kept in this pass and not left to the next trigger.
        if (records.length > head.length || resolutions.some((resolution) => resolution.rewind !== undefined)) continue;

        // Re-read rather than concluding the queue is empty because this batch
        // was the whole of what was read at the top. A write committed while
        // the request was in flight is not in `records`, and claiming "Synced"
        // over a mutation that is still waiting is the exact dishonesty this
        // state exists to prevent. One indexed read per drain, not per mutation.
        const beforeDrainRead = enqueues;
        notePendingFrom((await live(() => readPending(db))).length, beforeDrainRead);
        // A group this push did not finish speaking of is said before the pass ends (g108).
        if (ledgers.length > 0 && sayGroups(await live(() => readGroups(db)), []).groups.length > 0) continue;

        announce("idle");
        return;
      } catch (error) {
        if (error instanceof Stopped || stopped) return;

        // No verdict is coming for what was on the wire, so it stops being
        // frozen. Otherwise every retry while offline would leave another
        // untouchable record behind and the queue would grow by the tap.
        if (inflight.length > 0) {
          await live(() => releaseInflight(db, inflight));
          inflight = [];
        }

        const failure = classify(error);
        // A command charged by its size waits out the window on its own, and
        // everything that must not overtake it waits with it; the rest drains.
        if (error instanceof ApiError && error.status === 429 && active[0]
          && (active[0].op === "import" || isListOrderChange(active[0]))) {
          deferred.add(active[0].seq);
          continue;
        }

        if (failure === "unavailable" || failure === "forbidden") {
          await finish(failure);
          return;
        }

        if (failure === "session_expired" || failure === "name_required" || failure === "visitor_changed" || failure === "not_added") {
          hold(failure, messageOf(error));
          return;
        }

        if (failure === "auth") {
          // Nothing is discarded and nothing is reverted — the work is still
          // good, there is just nobody to attribute it to until they sign in.
          paused = true;
          announce("paused");
          emit({ type: "problem", message: messageOf(error) });
          return;
        }

        // A refused pull has no mutation to isolate. Looping on it would ask
        // the same question forever, so it fails like a broken server.
        if (failure === "poison" && active.length === 0) {
          announce("failed");
          emit({ type: "problem", message: messageOf(error) });
          return;
        }

        if (failure === "poison") {
          const head = active;
          if (head.length > 1) {
            // Find which one it is rather than throwing away the batch.
            limit = 1;
            continue;
          }
          const record = head[0];
          if (record) {
            // Reported, like the success path above: the revert took the row
            // out of the mirror, and a screen still drawing it is showing
            // something this device no longer has.
            report(await live(() => resolve([record.op === "import"
              ? { seq: record.seq, imported: { id: record.operationId!, rejected: messageOf(error) }, ...settledBy(record, undefined, "refused") }
              // A refused ordering puts the setting back, and fetches the list
              // again from the start: the categories this device filled with it
              // were never the server's, and no delta came to say so (g81).
              // So does any whole action refused this way (g107): no delta comes
              // with it, and the row put back may hold an earlier refused change.
              : { seq: record.seq, revert: revertTo(record), ...settledBy(record, undefined, "refused"),
                ...(isListOrderChange(record) || record.actionVersion !== undefined ? { rewind: 0 } : {}) }])));
            emit({ type: "problem", message: messageOf(error) });
          }
          // The offender is gone; the rest of the queue can go back to
          // travelling five hundred at a time.
          limit = MAX_BATCH;
          continue;
        }

        if (failure === "server" && attempt >= SERVER_BACKOFF.length) {
          announce("failed");
          emit({ type: "problem", message: messageOf(error) });
          return;
        }

        announce(failure === "offline" ? "offline" : "syncing");
        await sit(backoff(attempt, failure));
        attempt += 1;
      }
    }
  }

  function sync(): Promise<void> {
    if (stopped || paused) return Promise.resolve();
    // An `online` event should cut a minute of backoff short, not queue behind it.
    wake?.();

    if (running) {
      again = true;
      return running;
    }

    running = (async () => {
      do {
        again = false;
        await pass();
      } while (again && !stopped && !paused);
    })()
      .catch((error: unknown) => {
        // `pass` handles every failure it expects. Anything left is a bug here,
        // and a bug that arrives as an unhandled rejection is a bug nobody sees.
        emit({ type: "problem", message: messageOf(error) });
      })
      .finally(() => {
        running = undefined;
      });

    return running;
  }

  return {
    sync,
    queued() {
      outstanding += 1;
      enqueues += 1;
      notePending(true);
    },
    durable() {
      if (outstanding > 0) outstanding -= 1;
    },
    resume() {
      if (!paused || ended) return;
      paused = false;
      // No status announced here on purpose: `idle` is the engine saying a pass
      // finished, which is what tells a caller the mirror has been filled at
      // least once. Saying it now would claim a sync that has not happened.
      void sync();
    },
    status: () => status,
    busy: () => running !== undefined,
    stop() {
      stopped = true;
      wake?.();
    },
  };
}

/** Room kept in a batch for the groups its push may carry: four full manifests, each child also named as not coming. */
export const GROUP_RESERVE_BYTES = MAX_PUSH_GROUPS * MAX_GROUP_CHILDREN * 48;

/**
 * What a push says of this device's groups (g108), and the records it may
 * carry with them.
 *
 * A group's manifest has to reach the server no later than its first child:
 * a child that arrives first is recorded as an ordinary change, outside its
 * group. So every group with a child in the batch and no acknowledged
 * manifest is declared in this push, and a push names at most
 * `MAX_PUSH_GROUPS`: the batch is cut before the first record that would
 * need one more. The rest wait for the next push, in order.
 *
 * Beside those, a group is spoken of when a child of it is in the batch, so
 * the reply says whether that child closed it; when it has a child the server
 * has not been told will never come; and when nothing of it is queued any
 * more, which asks once whether it closed. The manifest is sent until a reply
 * has acknowledged it, and every child not coming is named again with it.
 */
export function sayGroups(ledgers: readonly GroupLedger[], batched: readonly Pending[]): { head: Pending[]; groups: SyncGroupInput[] } {
  const byId = new Map(ledgers.map((ledger) => [ledger.id, ledger]));
  const chosen: GroupLedger[] = [];
  const take = (ledger: GroupLedger) => { if (!chosen.includes(ledger)) chosen.push(ledger); };

  let head = [...batched];
  for (const [at, record] of batched.entries()) {
    const ledger = record.group ? byId.get(record.group.id) : undefined;
    if (!ledger || ledger.declared || chosen.includes(ledger)) continue;
    if (chosen.length >= MAX_PUSH_GROUPS) { head = batched.slice(0, at); break; }
    take(ledger);
  }
  const room = () => chosen.length < MAX_PUSH_GROUPS;
  for (const record of head) {
    const ledger = record.group ? byId.get(record.group.id) : undefined;
    if (ledger && room()) take(ledger);
  }
  for (const ledger of ledgers) {
    if (!room()) break;
    const pending = Object.values(ledger.children).some((child) => child.state === "pending");
    if (!ledger.declared || untold(ledger).length > 0 || !pending) take(ledger);
  }

  return { head, groups: chosen.map((ledger) => {
    const children = Object.entries(ledger.children).sort(([, a], [, b]) => a.index - b.index);
    // With the manifest, every child not coming: a server that did not know the group has heard none of them.
    const notSent = ledger.declared ? untold(ledger)
      : children.filter(([, child]) => child.state === "unsent" || child.state === "refused").map(([, child]) => child.index);
    return { id: ledger.id, kind: ledger.kind,
      ...(ledger.declared ? {} : { children: children.map(([operationId]) => operationId) }),
      ...(notSent.length ? { notSent } : {}) };
  }) };
}

/**
 * Each group a push spoke of with the server's answer to it. A group the
 * reply does not name gets no acknowledgement: nothing is concluded about it,
 * and it is said again.
 */
export function acknowledged(said: readonly SyncGroupInput[], replies: readonly SyncGroupReply[] | undefined): GroupAck[] {
  if (!Array.isArray(replies)) return [];
  const byId = new Map(replies.map((reply) => [String(reply.id).toLowerCase(), reply]));
  return said.flatMap((group) => {
    const reply = byId.get(group.id.toLowerCase());
    if (!reply || !["open", "closed", "unknown", "refused"].includes(reply.status)) return [];
    return [{ id: group.id, status: reply.status, notSent: group.notSent ?? [] }];
  });
}

/**
 * Verdict by index, which is the only reason the server sends an index at all —
 * so it is read rather than assumed.
 *
 * Taking the array's order as the answer would work today, because the server
 * builds its results by mapping over the batch. But then `index` would be a
 * field the protocol carries and nobody checks, and the first time anything
 * between here and there reorders or repeats a reply, the wrong `seq` is
 * settled or reverted and nothing notices.
 *
 * Every shape of wrong answer is refused rather than half-believed — a missing
 * verdict, a repeated one, an index for a mutation that was not in this batch,
 * or one whose echoed `op` and `id` name a different mutation than the slot it
 * claims. Treating any of them as "not applied" would revert a mutation the
 * server may well have taken.
 *
 * The echo is checked rather than read: nothing here acts on `result.op` or
 * `result.id` — the record is chosen by index and everything else comes from
 * what this device queued. They are the server's own statement of which
 * mutation it answered, so disagreeing with the index means one of the two is
 * wrong and there is no way to tell which.
 */
function reconcile(
  head: Pending[],
  sent: Mutation[],
  results: MutationResult[],
): { resolutions: Resolution[]; problems: string[] } {
  if (results.length !== head.length) {
    throw new ProtocolError("The server answered a different number of mutations than were sent.");
  }

  const byIndex = new Map(results.map((result) => [result.index, result]));
  if (byIndex.size !== results.length) {
    throw new ProtocolError("The server answered one mutation twice.");
  }

  const resolutions: Resolution[] = [];
  const problems: string[] = [];

  head.forEach((record, index) => {
    const result = byIndex.get(index);
    if (!result) throw new ProtocolError("The server left a mutation unanswered.");

    const mutation = sent[index];
    if (!mutation || result.op !== mutation.op || result.id !== mutation.id) {
      throw new ProtocolError("The server's verdict names a different mutation than it answers.");
    }

    if (result.status === "applied") {
      if (mutation.op === "list.import") {
        if (!("list" in result) || result.list?.id !== mutation.id || !("items" in result) || !Array.isArray(result.items)) throw new ProtocolError("Incomplete import verdict.");
        const ids = new Set(result.items.map(row => row.id));
        if (ids.size !== mutation.items.length || result.items.length !== ids.size || mutation.items.some(row => !ids.has(row.id))) throw new ProtocolError("Incomplete imported items.");
        resolutions.push({ seq: record.seq, imported: { id: mutation.operationId }, ...settledBy(record, result.action) });
        return;
      }
      // The row itself arrives in the delta of the same reply. Writing it here
      // too would be a second path to the same place, free to disagree.
      resolutions.push({ seq: record.seq, ...settledBy(record, result.action) });
      return;
    }
    const refusal = settledBy(record, undefined, result.reason);

    if (result.status !== "rejected" || typeof result.message !== "string") throw new ProtocolError("Invalid mutation verdict.");
    if (record.op === "import") {
      resolutions.push({ seq: record.seq, imported: { id: record.operationId!, rejected: result.message }, ...refusal });
      problems.push(`${result.message} Your import is saved under Lists for recovery.`);
      return;
    }
    // Already gone is the outcome that was asked for.
    if (record.op === "delete") {
      resolutions.push({ seq: record.seq, ...refusal });
      return;
    }

    if (result.reason === "not_found" || result.reason === "list_not_found") {
      // Another device deleted it. Restoring `before` would resurrect something
      // that does not exist, and the tombstone is in this very reply, so the
      // row is about to vanish from the screen with an explanation the user
      // already has: they deleted it.
      resolutions.push({ seq: record.seq, revert: discard(record), ...refusal });
      return;
    }

    // Everything else — a quota rejection, when step 10 adds them — is the
    // user's change being refused, and is worth saying out loud.
    resolutions.push({ seq: record.seq, revert: revertTo(record), ...refusal,
      // A whole action's `before` is the row as this device drew it, which
      // may hold an earlier change of the same list that was refused in
      // another answer, or a key this device made up for an earlier move
      // (g107). No reply carries a list's current row as it does an item's,
      // so a refused change to a list fetches the lists again.
      ...(record.actionVersion !== undefined && record.entity === "list" ? { rewind: 0 } : {}) });
    problems.push(result.message);
  });

  return { resolutions, problems };
}

/**
 * What the server said became of a whole action (g107); nothing for a folded
 * record. A receipt that names another operation than the one sent is refused
 * like any other verdict that answers the wrong mutation.
 */
function settledBy(record: Pending, receipt: SyncActionReceipt | undefined, refused?: string): { settled?: Settled } {
  if (record.actionVersion === undefined || !record.operationId) return {};
  const operationId = record.operationId;
  const group = record.group ? { group: record.group.id } : {};
  if (refused !== undefined) return { settled: { operationId, state: "refused", recorded: false, replay: false, reason: refused, ...group } };
  if (!receipt) return { settled: { operationId, state: "applied", recorded: false, replay: false, ...group } };
  if (typeof receipt.operationId !== "string" || receipt.operationId.toLowerCase() !== operationId.toLowerCase()
    || (receipt.outcome !== "applied" && receipt.outcome !== "unchanged")) {
    throw new ProtocolError("The server's receipt names a different operation than it answers.");
  }
  return { settled: { operationId, state: receipt.outcome, recorded: true, replay: receipt.replay === true, ...group } };
}

function discard(record: Pending): RowWrite {
  return record.entity === "list"
    ? { entity: "list", op: "remove", id: record.id }
    : { entity: "item", op: "remove", id: record.id };
}

function revertTo(record: Pending): RowWrite {
  if (record.before === null) return discard(record);
  return record.entity === "list"
    ? { entity: "list", op: "put", row: record.before as List }
    : { entity: "item", op: "put", row: record.before as Item };
}

function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : "Something went wrong syncing.";
}

const FOCUS_DEBOUNCE = 500;

/**
 * App start, coming back online, and coming back to the app. **No polling
 * timer** here — a list app that has not changed has nothing to ask about, and
 * a phone that wakes every thirty seconds is a phone with a flat battery. The
 * one exception is a list someone else may be changing while it is on screen
 * (g71), and `attachPoller` below is the whole of it.
 */
export function attachTriggers(engine: Engine): () => void {
  let timer: ReturnType<typeof setTimeout> | undefined;

  // `focus` is what a desktop tab fires; `visibilitychange` is what a PWA fires
  // when it comes back from the background. Both, and both at once when a
  // laptop lid opens — hence the debounce.
  const nudge = () => {
    if (timer !== undefined) return;
    timer = setTimeout(() => {
      timer = undefined;
      void engine.sync();
    }, FOCUS_DEBOUNCE);
  };
  const onVisible = () => {
    if (document.visibilityState === "visible") nudge();
  };
  const onOnline = () => {
    void engine.sync();
  };

  window.addEventListener("online", onOnline);
  window.addEventListener("focus", nudge);
  document.addEventListener("visibilitychange", onVisible);

  return () => {
    if (timer !== undefined) clearTimeout(timer);
    window.removeEventListener("online", onOnline);
    window.removeEventListener("focus", nudge);
    document.removeEventListener("visibilitychange", onVisible);
  };
}

/**
 * The refresh contract for a list someone else may be changing (g71): a
 * shared list a visitor has open, or an owner's open list while one of its
 * links works.
 *
 * - Every `POLL_MS`, jittered by `POLL_JITTER` either way, measured from the
 *   end of the last refresh, so two never overlap and a slow one pushes the
 *   next back rather than stacking behind it.
 * - Only while the page is visible. Hidden, there is no timer at all; shown
 *   again, `attachTriggers` refreshes at once and the timer starts over.
 * - A refresh that fails backs off through `POLL_BACKOFF`, jittered the same
 *   way, and the first clean one returns to `POLL_MS`. Offline, the engine's
 *   own pass waits for the connection, and the timer waits for the pass.
 * - Nothing while the scope is paused, held or ended, until `reevaluate()`.
 *
 * Healthy, a change made elsewhere is on screen within `HEALTHY_STALENESS_MS`:
 * the longest jittered interval, plus the request.
 */
export const POLL_MS = 10_000;
export const POLL_JITTER = 0.1;
export const POLL_BACKOFF = [20_000, 40_000, 80_000, 120_000] as const;
export const HEALTHY_STALENESS_MS = 12_000;

export interface Poller {
  /** Starts, keeps or stops the timer to match `active()` and the page. */
  reevaluate(): void;
  /** For good. Nothing reschedules after this, a refresh already running included. */
  stop(): void;
  /** A refresh is waiting on the timer or running now. */
  running(): boolean;
}

export interface PollerOptions {
  /** Whether a list that may change under the person is on screen. */
  active(): boolean;
  /** Injected for tests. */
  random?: () => number;
  document?: Pick<Document, "visibilityState" | "addEventListener" | "removeEventListener">;
}

export function attachPoller(engine: Engine, options: PollerOptions): Poller {
  const random = options.random ?? Math.random;
  const doc = options.document ?? document;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let ticking = false;
  let stopped = false;
  let failures = 0;

  const visible = () => doc.visibilityState === "visible";
  const waiting = () => {
    const status = engine.status();
    return status === "paused" || status === "held" || status === "ended";
  };

  function clear(): void {
    if (timer !== undefined) clearTimeout(timer);
    timer = undefined;
  }

  function schedule(): void {
    clear();
    if (stopped || ticking || !visible() || waiting() || !options.active()) return;
    const base = failures === 0 ? POLL_MS : POLL_BACKOFF[Math.min(failures, POLL_BACKOFF.length) - 1]!;
    timer = setTimeout(() => void tick(), Math.round(base * (1 - POLL_JITTER + random() * 2 * POLL_JITTER)));
  }

  async function tick(): Promise<void> {
    timer = undefined;
    if (stopped || !visible() || waiting() || !options.active()) return;
    ticking = true;
    try {
      // Joins a pass already running rather than starting a second.
      await engine.sync();
    } finally {
      ticking = false;
    }
    if (stopped) return;
    const status = engine.status();
    failures = status === "failed" || status === "offline" ? failures + 1 : 0;
    schedule();
  }

  const onVisibility = () => (visible() ? schedule() : clear());
  doc.addEventListener("visibilitychange", onVisibility);

  return {
    reevaluate() {
      if (timer === undefined || !options.active() || !visible() || waiting()) schedule();
    },
    stop() {
      stopped = true;
      clear();
      doc.removeEventListener("visibilitychange", onVisibility);
    },
    running: () => timer !== undefined || ticking,
  };
}
